import { SafePromise } from "@pdfking/tfunc"
import type { PDFImage } from "pdf-lib"
import { PDFDocument } from "pdf-lib"

type AttemptFunction = (
  bytes: Uint8Array,
  resolution?: number
) => Promise<PDFDocument>

const image_to_pdf = async (
  method: "embedJpg" | "embedPng",
  bytes: Uint8Array,
  res: number
): Promise<PDFDocument> => {
  const pdf = await PDFDocument.create()
  const image: PDFImage = await pdf[method](bytes)
  const { width, height } = image.scale(72 / res)
  const page = pdf.addPage([width, height])

  page.drawImage(image, {
    x: 0,
    y: 0,
    width,
    height,
  })

  return pdf
}

const attempts: Record<string, AttemptFunction> = {
  "application/pdf": (bytes: Uint8Array) => PDFDocument.load(bytes),
  "image/jpeg": (bytes: Uint8Array, res: number) =>
    image_to_pdf("embedJpg", bytes, res),
  "image/png": (bytes: Uint8Array, res: number) =>
    image_to_pdf("embedPng", bytes, res),
}

const iterate_attempts = function* (
  content_type: string
): Iterable<AttemptFunction> {
  // First try the specific content type if it exists
  if (attempts[content_type]) {
    yield attempts[content_type]
  }

  // Then try all other content types we haven't tried yet
  for (const [type, func] of Object.entries(attempts)) {
    if (type !== content_type) {
      yield func
    }
  }
}

export const BytesToPdf = async (
  bytes: Uint8Array,
  content_type: string,
  res: number
): Promise<PDFDocument> => {
  for (const func of iterate_attempts(content_type)) {
    const { value, error } = await SafePromise(func(bytes, res))
    if (error) {
      console.warn("attempting failed", error)
    } else {
      return value
    }
  }
  throw new Error("No suitable conversion method found")
}
